import path from 'path';

import { useTranslation } from 'react-i18next';

import { Card, TwoLineGrid } from '~components';
import { pagePathSegments } from '~constants';
import { UserRoleEnum } from '~enums';
import { getUserRoleLabel } from '~enums/userRoleEnum';
import useAuthorize from '~hooks/useAuthorize';
import User from '~interfaces/user';
import i18n from '~lib/i18n';
import { ifNullOrEmpty } from '~utils/stringUtils';

interface Props {
	user: User | undefined;
	isLoading: boolean;
}

const UserInformationCard = ({ user, isLoading }: Props) => {
	const { t } = useTranslation();
	const { authorize } = useAuthorize();
	const isSuperAdmin = authorize([UserRoleEnum.SuperAdmin]);

	const organisationName = user?.organisationName?.trim() || user?.organisationId;
	return (
		<Card title={t('user.fields.generalInformation', { ns: 'entities' })} sx={{ height: '100%' }}>
			<TwoLineGrid
				isLoading={isLoading}
				twoLineItems={[
					{
						primary: t('general.name', { ns: 'entities' }),
						secondary: `${user?.firstName} ${user?.lastName}`,
					},
					{
						primary: t('user.fields.email', { ns: 'entities' }),
						secondary: ifNullOrEmpty(user?.emailAddress),
					},
					{
						primary: t('user.fields.phone', { ns: 'entities' }),
						secondary: ifNullOrEmpty(user?.phoneNumber),
					},
					{
						primary: t('user.fields.language', { ns: 'entities' }),
						secondary:
							new Intl.DisplayNames(i18n.language, { type: 'language' }).of(
								user?.culture?.split('-')[0] || 'nl'
							) || ifNullOrEmpty(user?.culture),
					},
					{
						primary: t('organisation.labels.single', { ns: 'entities' }),
						secondary: ifNullOrEmpty(organisationName),
						link:
							user?.organisationId && isSuperAdmin ?
								path.join(
									'/',
									pagePathSegments.Organisations,
									user?.organisationId?.toString() ?? '',
									pagePathSegments.General
								)
							:	undefined,
					},
					{
						primary: t('user.fields.department', { ns: 'entities' }),
						secondary: ifNullOrEmpty(user?.department),
					},
					{
						primary: t('user.fields.role', { ns: 'entities' }),
						secondary:
							user?.role ? getUserRoleLabel(user?.role, t) : t('general.none', { ns: 'entities' }),
					},
				]}
			/>
		</Card>
	);
};

export default UserInformationCard;
